import { createContext, useContext, useEffect, useState } from "react";

import { apiClient, API_PREFIX } from "@/lib/api";
import { clearAccessToken, getAccessToken, setAccessToken } from "@/lib/auth";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    const syncFromStorage = async () => {
      const token = getAccessToken();
      if (!token) {
        setUser(null);
        setIsInitializing(false);
        return;
      }

      try {
        const response = await apiClient.get(`${API_PREFIX}/auth/me`, {
          skipAuthLogout: true,
        });
        setUser(response.data);
      } catch (_error) {
        clearAccessToken();
        setUser(null);
      } finally {
        setIsInitializing(false);
      }
    };

    const handleAuthChanged = () => {
      if (!getAccessToken()) {
        setUser(null);
      }
    };

    syncFromStorage();
    window.addEventListener("kcg-auth-changed", handleAuthChanged);
    return () => {
      window.removeEventListener("kcg-auth-changed", handleAuthChanged);
    };
  }, []);

  const login = async ({ email, password }) => {
    const response = await apiClient.post(
      `${API_PREFIX}/auth/login`,
      { email, password },
      { skipAuthLogout: true },
    );
    setAccessToken(response.data.access_token);
    setUser(response.data.user);
    return response.data.user;
  };

  const logout = () => {
    clearAccessToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: Boolean(user),
        isInitializing,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}

